import React, { useEffect, useState, useCallback, useRef } from 'react';
import { get_all_active_attendance_type } from '../../../../app/pages/Employees/api/PersonalDetailsApi';
import { get_all_shifts } from '../../../../app/pages/Settings/shifts/api/shiftsApi'; // Assuming this is for general shift list if needed elsewhere
import { add_attendance_shifts } from '../../../partials/widgets/api/widgetApi';
import { formatDateWithTime24h } from '../../../../app/helper-functions/DateFunction';
import { get_all_active_shifts } from '../../../../app/pages/Employees/attendance/api/attendanceApi'; // Used for the dropdown
import { get_attendance_options } from '../../../../app/pages/Employees/attendance-management-admin/api/employeesAttendanceApi';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';

const getStorageKey = (key: string) => {
  const currentUser = localStorage.getItem("kt-auth-react-v");
  const currentUserData = currentUser ? JSON.parse(currentUser) : null;
  const currentUserId = currentUserData?.user?.id || currentUserData?.id;
  return currentUserId ? `${key}_${currentUserId}` : key;
};

type ShiftStatus =
  | 'not_started'
  | 'selecting_shift'
  | 'in_progress'
  | 'selecting_break_to_start'
  | 'on_break'
  | 'selecting_break_to_end'
  | 'selecting_end_shift'
  | 'ended';

type LoadingAction =
  | 'fetchData'
  | 'initiateStartShift'
  | 'confirmStartShift'
  | 'initiateStartBreak'
  | 'confirmStartBreak'
  | 'initiateEndBreak'
  | 'confirmEndBreak'
  | 'initiateEndShift'
  | 'confirmEndShift'
  | null;

interface AttendanceType {
  id: string | number;
  title: string;
  type?: 'shift_start' | 'shift_end' | 'break_start' | 'break_end';
}

interface Shift {
  id: string | number;
  title: string;
  shift_title?: string; // Accommodate potential variations in API response
}

export const ShiftControls: React.FC = () => {
   const [shiftStatus, setShiftStatus] = useState<ShiftStatus>('not_started');
   const [activeLoadingAction, setActiveLoadingAction] = useState<LoadingAction>(null);
   const [attendanceTypes, setAttendanceTypes] = useState<AttendanceType[]>([]);
   const [shiftsList, setShiftsList] = useState<Shift[]>([]);
   const [shiftStartTime, setShiftStartTime] = useState<string | null>(null);
   const [selectedShiftId, setSelectedShiftId] = useState<string | number | null>(null);
   const [selectedShiftTypeId, setSelectedShiftTypeId] = useState<string | number | null>(null);
   const [selectedBreakTypeId, setSelectedBreakTypeId] = useState<string | number | null>(null);
   const [activeShiftInstanceId, setActiveShiftInstanceId] = useState<string | number | null>(null);
   const [tempSelectedShiftId, setTempSelectedShiftId] = useState<string | number | null>(null);
   const [tempSelectedShiftTypeId, setTempSelectedShiftTypeId] = useState<string | number | null>(null);
   const [tempSelectedBreakTypeId, setTempSelectedBreakTypeId] = useState<string | number | null>(null);
   const [statusBeforeSelection, setStatusBeforeSelection] = useState<ShiftStatus>('not_started');

   const currentUser = localStorage.getItem('auth_user_id');
   const userId = currentUser ? JSON.parse(currentUser) : null;

   const lastProcessedAttendanceRef = useRef<any>(undefined);

   const getLocalStorageItem = useCallback((key: string) => localStorage.getItem(getStorageKey(key)), []);
   const setLocalStorageItem = useCallback((key: string, value: string) => localStorage.setItem(getStorageKey(key), value), []);
   const removeLocalStorageItem = useCallback((key: string) => localStorage.removeItem(getStorageKey(key)), []);

   const handleCancelSelection = useCallback(() => {
      if (activeLoadingAction) return;
      setShiftStatus(statusBeforeSelection);
      setTempSelectedShiftId(null);
      setTempSelectedShiftTypeId(null);
      setTempSelectedBreakTypeId(null);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeLoadingAction, statusBeforeSelection]);


   const processAttendanceData = useCallback((response: any) => {
    if (!response || !response.status || !response.data) {
        const msg = response?.message || 'Failed to retrieve attendance options or invalid response.';
        // console.error("processAttendanceData error:", msg, response);
        toast.error(msg);
        return;
    }
    const { lastAttendance } = response.data;
    const newLA = lastAttendance;
    const oldLA = lastProcessedAttendanceRef.current;
    let noMeaningfulChange = false;
    if (newLA === null && oldLA === null) noMeaningfulChange = true;
    else if (newLA && oldLA) {
        if (
            newLA.id === oldLA.id &&
            newLA.shift_id === oldLA.shift_id &&
            newLA.attendance_type_id === oldLA.attendance_type_id &&
            newLA.attendance_datetime === oldLA.attendance_datetime &&
            newLA.attendance_type === oldLA.attendance_type // raw type from payload
        ) noMeaningfulChange = true;
    }
    if (oldLA !== undefined && noMeaningfulChange) {
        // console.log("ShiftControls: API data for lastAttendance is effectively unchanged. Skipping state updates.");
        return;
    }
    // console.log("ShiftControls: API data for lastAttendance changed or initial load. Processing updates.", {newLA, oldLA});

    let processingError = false;
    try {
        if (lastAttendance) {
            const {
                id: apiLastAttendanceId,
                shift_id: apiShiftId,
                attendance_type_id: apiAttendanceTypeId,
                attendance_datetime: apiAttendanceDatetime,
                attendance_type: apiAttendanceTypeFromPayload,
            } = lastAttendance;

            let effectiveType: 'shift_start' | 'break_start' | 'break_end' | 'shift_end' | 'unknown' = 'unknown';
            switch (apiAttendanceTypeFromPayload) {
                case 'shift_start': case 'check-in': effectiveType = 'shift_start'; break;
                case 'break_start': case 'pause': case 'pause_start': effectiveType = 'break_start'; break;
                case 'break_end': case 'resume': case 'pause_end': effectiveType = 'break_end'; break;
                case 'shift_end': case 'check-out': effectiveType = 'shift_end'; break;
                default: effectiveType = 'unknown'; break;
            }

            if (effectiveType === 'shift_start') {
                setShiftStatus('in_progress'); setShiftStartTime(apiAttendanceDatetime);
                setSelectedShiftId(apiShiftId); setSelectedShiftTypeId(apiAttendanceTypeId);
                setActiveShiftInstanceId(apiLastAttendanceId);
                setLocalStorageItem('shiftStartTime', apiAttendanceDatetime);
                setLocalStorageItem('selectedShiftId', String(apiShiftId));
                setLocalStorageItem('selectedShiftTypeId', String(apiAttendanceTypeId));
                setLocalStorageItem('activeShiftInstanceId', String(apiLastAttendanceId));
                setSelectedBreakTypeId(null); removeLocalStorageItem('breakStartTime'); removeLocalStorageItem('selectedBreakTypeId');
            } else if (effectiveType === 'break_start') {
                setShiftStatus('on_break'); setSelectedBreakTypeId(apiAttendanceTypeId);
                setLocalStorageItem('selectedBreakTypeId', String(apiAttendanceTypeId));
                setLocalStorageItem('breakStartTime', apiAttendanceDatetime);
                setSelectedShiftId(apiShiftId); setLocalStorageItem('selectedShiftId', String(apiShiftId));
                const storedParentShiftStartTime = getLocalStorageItem('shiftStartTime');
                const storedParentShiftTypeId = getLocalStorageItem('selectedShiftTypeId');
                const storedParentActiveShiftInstanceId = getLocalStorageItem('activeShiftInstanceId');
                if (storedParentShiftStartTime) setShiftStartTime(storedParentShiftStartTime);
                if (storedParentShiftTypeId) setSelectedShiftTypeId(storedParentShiftTypeId);
                if (storedParentActiveShiftInstanceId) setActiveShiftInstanceId(storedParentActiveShiftInstanceId);
            } else if (effectiveType === 'break_end') {
                setShiftStatus('in_progress'); setSelectedShiftId(apiShiftId);
                setLocalStorageItem('selectedShiftId', String(apiShiftId));
                const storedParentShiftStartTime = getLocalStorageItem('shiftStartTime');
                const storedParentShiftTypeId = getLocalStorageItem('selectedShiftTypeId');
                const storedParentActiveShiftInstanceId = getLocalStorageItem('activeShiftInstanceId');
                if (storedParentShiftStartTime) setShiftStartTime(storedParentShiftStartTime);
                if (storedParentShiftTypeId) setSelectedShiftTypeId(storedParentShiftTypeId);
                if (storedParentActiveShiftInstanceId) setActiveShiftInstanceId(storedParentActiveShiftInstanceId);
                setSelectedBreakTypeId(null); removeLocalStorageItem('breakStartTime'); removeLocalStorageItem('selectedBreakTypeId');
            } else if (effectiveType === 'shift_end' || effectiveType === 'unknown') {
                setShiftStatus('not_started'); setShiftStartTime(null); setSelectedShiftId(null);
                setSelectedShiftTypeId(null); setActiveShiftInstanceId(null); setSelectedBreakTypeId(null);
                removeLocalStorageItem('shiftStartTime'); removeLocalStorageItem('selectedShiftId');
                removeLocalStorageItem('selectedShiftTypeId'); removeLocalStorageItem('activeShiftInstanceId');
                removeLocalStorageItem('breakStartTime'); removeLocalStorageItem('selectedBreakTypeId');
            }

            if (shiftStatus.startsWith('selecting_')) {
                let newBaseStatusAfterSync: ShiftStatus = 'not_started';
                if (effectiveType === 'shift_start' || effectiveType === 'break_end') newBaseStatusAfterSync = 'in_progress';
                else if (effectiveType === 'break_start') newBaseStatusAfterSync = 'on_break';
                else if (effectiveType === 'shift_end' || effectiveType === 'unknown') newBaseStatusAfterSync = 'not_started'; // or 'ended'

                let selectionStillValid = false;
                if (newBaseStatusAfterSync === 'not_started' && shiftStatus === 'selecting_shift') selectionStillValid = true;
                else if (newBaseStatusAfterSync === 'in_progress' && (shiftStatus === 'selecting_break_to_start' || shiftStatus === 'selecting_end_shift')) selectionStillValid = true;
                else if (newBaseStatusAfterSync === 'on_break' && (shiftStatus === 'selecting_break_to_end' || shiftStatus === 'selecting_end_shift')) selectionStillValid = true;
                else if (newBaseStatusAfterSync === statusBeforeSelection) selectionStillValid = true;

                if (!selectionStillValid && activeLoadingAction === null) { // Only cancel if no action is currently processing
                    // console.log(`Cancelling selection due to state mismatch. Current UI: ${shiftStatus}, Synced Base: ${newBaseStatusAfterSync}, Before Selection: ${statusBeforeSelection}`);
                    handleCancelSelection();
                }
            }
        } else { // No lastAttendance
            setShiftStatus('not_started'); setShiftStartTime(null); setSelectedShiftId(null);
            setSelectedShiftTypeId(null); setActiveShiftInstanceId(null); setSelectedBreakTypeId(null);
            removeLocalStorageItem('shiftStartTime'); removeLocalStorageItem('selectedShiftId');
            removeLocalStorageItem('selectedShiftTypeId'); removeLocalStorageItem('activeShiftInstanceId');
            removeLocalStorageItem('breakStartTime'); removeLocalStorageItem('selectedBreakTypeId');
            if (shiftStatus.startsWith('selecting_') && activeLoadingAction === null) { // Only cancel if no action is currently processing
                handleCancelSelection();
            }
        }
    } catch (error) {
        // console.error("Error processing attendance data:", error);
        toast.error("An error occurred while updating attendance state.");
        processingError = true;
    } finally {
        if (!processingError) {
            lastProcessedAttendanceRef.current = lastAttendance ? { ...lastAttendance } : null;
        }
    }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
    getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem,
    shiftStatus, statusBeforeSelection, handleCancelSelection, // Added handleCancelSelection
    // State setters are stable, no need to list them
    activeLoadingAction // Added activeLoadingAction to avoid cancelling selection if an action is in progress
   ]);

   const {
    // data: attendanceOptionsData, // Not directly used after onSuccess
    isLoading: isAttendanceQueryInitiallyLoading, // Renamed for clarity
    // isFetching: isAttendanceQueryFetching, // We'll manage loading state more granularly
    // refetch, // Not directly calling refetch
} = useQuery(
    ['attendance-options', userId],
    () => {
        if (!userId) return Promise.reject(new Error("User ID not available for fetching attendance options."));
        return get_attendance_options(userId);
    },
    {
        enabled: !!userId,
        refetchOnWindowFocus: true,
        refetchInterval: 5000, // Or your desired interval; 500ms is very frequent
        onSuccess: processAttendanceData,
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Error fetching attendance options.';
            toast.error(errorMessage);
        }
    }
);

   const attendanceOptionsMutation = useMutation(
    () => {
        if (!userId) return Promise.reject(new Error("User ID not available for fetching attendance options."));
        return get_attendance_options(userId);
    },
    {
        onSuccess: processAttendanceData,
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Error fetching attendance options.';
            toast.error(errorMessage);
        },
    }
 );

  useEffect(() => {
    if (userId && lastProcessedAttendanceRef.current === undefined) { // Fetch on initial load or user change if no data yet
      // console.log("User ID available, attempting to fetch initial attendance options via mutation:", userId);
      attendanceOptionsMutation.mutate();
    } else if (!userId) {
      // console.log("User ID not available. Resetting state.");
      setShiftStatus('not_started');
      lastProcessedAttendanceRef.current = undefined; // Clear last processed ref if user logs out
      // Clear other states if necessary
      setShiftStartTime(null); setSelectedShiftId(null); setSelectedShiftTypeId(null);
      setActiveShiftInstanceId(null); setSelectedBreakTypeId(null);
      setShiftsList([]); setAttendanceTypes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // `attendanceOptionsMutation.mutate` is stable

  const fetchShiftData = useCallback(async (forceFetch = false): Promise<Shift[]> => {
    if (Array.isArray(shiftsList) && shiftsList.length > 0 && !forceFetch) return shiftsList;
   try {
     const response = await get_all_active_shifts(); // Using the correct active shifts API
     if (response?.data && Array.isArray(response.data)) {
         setShiftsList(response.data); return response.data;
     }
     setShiftsList([]); return [];
   } catch (error) { setShiftsList([]); throw error; }
}, [shiftsList]);

const fetchAttendanceTypes = useCallback(async (forceFetch = false): Promise<AttendanceType[]> => {
  if (Array.isArray(attendanceTypes) && attendanceTypes.length > 0 && !forceFetch) return attendanceTypes;
  try {
    const response = await get_all_active_attendance_type();
    if (response?.data && Array.isArray(response.data)) {
      const processedTypes = response.data.map((t: any) => ({ id: t.id, title: t.title, type: t.type as any }));
      setAttendanceTypes(processedTypes); return processedTypes;
    }
    setAttendanceTypes([]); return [];
  } catch (error) { setAttendanceTypes([]); throw error; }
}, [attendanceTypes]);


const fetchRequiredData = useCallback(async (stateToAction: ShiftStatus): Promise<{ types: AttendanceType[] | null, shifts: Shift[] | null }> => {
   setActiveLoadingAction('fetchData');
   let fetchedTypes: AttendanceType[] | null = null;
   let fetchedShifts: Shift[] | null = null;
   const promises: Promise<any>[] = [];
   let needsTypes = false;
   let needsShifts = false;

    switch(stateToAction) {
         case 'selecting_shift':
             needsShifts = true; needsTypes = true; break;
         case 'selecting_break_to_start':
         case 'selecting_break_to_end':
             needsTypes = true;
             if (!Array.isArray(shiftsList) || shiftsList.length === 0) needsShifts = true; // Also fetch shifts if not available
             break;
         case 'selecting_end_shift':
             needsTypes = true;
             if (!Array.isArray(shiftsList) || shiftsList.length === 0) needsShifts = true; // Also fetch shifts if not available
             break;
         default: break;
    }
   if (needsTypes) {
       promises.push(fetchAttendanceTypes(true).then(data => fetchedTypes = data));
   } else {
       fetchedTypes = attendanceTypes;
   }
   if (needsShifts) {
       promises.push(fetchShiftData(true).then(data => fetchedShifts = data));
   } else {
       fetchedShifts = shiftsList;
   }
   try {
       if (promises.length > 0) await Promise.all(promises);
       return { types: fetchedTypes, shifts: fetchedShifts };
   } catch (error) {
       toast.error("Error loading selection data.");
       return { types: null, shifts: null };
   } finally {
       setActiveLoadingAction(null);
   }
}, [fetchShiftData, fetchAttendanceTypes, shiftsList, attendanceTypes]);


  // --- Event Handlers ---
  const handleInitiateShiftStart = async () => {
    setStatusBeforeSelection(shiftStatus);
    setTempSelectedShiftId(null); setTempSelectedShiftTypeId(null);
    setActiveLoadingAction('initiateStartShift');
    try {
      const fetchedData = await fetchRequiredData('selecting_shift');
      if (!fetchedData.shifts || fetchedData.shifts.length === 0 || !fetchedData.types || fetchedData.types.length === 0) {
          throw new Error("Failed to load necessary data (shifts or types) for shift start selection.");
      }
      setShiftStatus('selecting_shift');
    } catch (error) {
      toast.error(`Error preparing shift start: ${error instanceof Error ? error.message : String(error)}.`);
      setShiftStatus(statusBeforeSelection);
    } finally {
       setActiveLoadingAction(null);
    }
  };

  const handleSelectShiftTitleDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setTempSelectedShiftId(event.target.value);
  };

  const handleSelectAttendanceTypeDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
      if (shiftStatus === 'selecting_shift' || shiftStatus === 'selecting_end_shift') {
        setTempSelectedShiftTypeId(event.target.value);
      } else if (shiftStatus === 'selecting_break_to_start' || shiftStatus === 'selecting_break_to_end') {
        setTempSelectedBreakTypeId(event.target.value);
      }
  };

 const handleConfirmShiftStart = async () => {
  if (!tempSelectedShiftId) { toast.warn("Shift Title must be selected."); return; }
  const shiftStartRecord = attendanceTypes.find(item => item.type === "shift_start");
  if (!shiftStartRecord) { toast.error("Configuration Error: 'Shift Start' attendance type not found."); return; }

  setActiveLoadingAction('confirmStartShift');
  const currDate = new Date();
  const startTimeStr = formatDateWithTime24h(currDate);
  try {
    const payload = {
      shift_id: tempSelectedShiftId,
      attendance_type_id: shiftStartRecord.id,
      attendance_datetime: startTimeStr,
      employee_attendance_notes: "Shift Started",
    };
    await add_attendance_shifts(payload);
    toast.success("Shift started successfully! Syncing...");
    attendanceOptionsMutation.mutate();
    setTempSelectedShiftId(null); setTempSelectedShiftTypeId(null);
  } catch (error:any) {
    const errorMsg = error?.response?.data?.message || (error instanceof Error ? error.message : String(error));
    toast.error(`Error starting shift: ${errorMsg}`);
  } finally {
    setActiveLoadingAction(null);
  }
};

  const handleInitiateBreakStart = async () => {
    const currentShiftIdFromState = selectedShiftId ?? getLocalStorageItem('selectedShiftId');
    if (!currentShiftIdFromState) { toast.error("Error: Cannot start break without an active shift."); return; }
    setStatusBeforeSelection(shiftStatus); setTempSelectedBreakTypeId(null);
    setActiveLoadingAction('initiateStartBreak');
    try {
      const fetchedData = await fetchRequiredData('selecting_break_to_start');
       if (!fetchedData.types || fetchedData.types.filter(t => t.type === 'break_start').length === 0) {
           throw new Error("Failed to load break types or no 'Break Start' types found.");
       }
      setShiftStatus('selecting_break_to_start');
    } catch (error) {
       toast.error(`Error preparing break start: ${error instanceof Error ? error.message : String(error)}`);
      setShiftStatus(statusBeforeSelection);
    } finally {
       setActiveLoadingAction(null);
    }
  };

  const handleConfirmBreakStart = async () => {
    if (!tempSelectedBreakTypeId) { toast.warn("Please select a Break Type."); return; }
    const currentShiftInstanceId = activeShiftInstanceId ?? getLocalStorageItem('activeShiftInstanceId');
    const currentShiftId = selectedShiftId ?? getLocalStorageItem('selectedShiftId');
    if (!currentShiftInstanceId) { toast.error("Error: Shift context lost (Instance ID). Please refresh."); return; }
    if (!currentShiftId) { toast.error("Error: Shift context lost (Shift ID). Please refresh."); return; }

    setActiveLoadingAction('confirmStartBreak');
    const breakStartTimeStr = formatDateWithTime24h(new Date());
    try {
      const breakStartPayload = {
          parent_attendance_id: currentShiftInstanceId,
          attendance_type_id: tempSelectedBreakTypeId,
          attendance_datetime: breakStartTimeStr,
          employee_attendance_notes: "Break Started",
          shift_id: currentShiftId,
      };
      await add_attendance_shifts(breakStartPayload);
      toast.success("Break started! Syncing...");
      attendanceOptionsMutation.mutate();
      setTempSelectedBreakTypeId(null);
    } catch (error:any) {
        const errorMsg = error?.response?.data?.message || (error instanceof Error ? error.message : String(error));
        toast.error(`Error starting break: ${errorMsg}`);
    } finally {
        setActiveLoadingAction(null);
    }
  };

  const handleInitiateBreakEnd = async () => {
     const currentShiftIdFromState = selectedShiftId ?? getLocalStorageItem('selectedShiftId');
     const currentBreakTypeIdFromState = selectedBreakTypeId ?? getLocalStorageItem('selectedBreakTypeId');
     if (!currentShiftIdFromState || !currentBreakTypeIdFromState) { toast.error("Error: Cannot end break. Missing shift or break information."); return; }
    setStatusBeforeSelection(shiftStatus); setTempSelectedBreakTypeId(null);
    setActiveLoadingAction('initiateEndBreak');
    try {
      const fetchedData = await fetchRequiredData('selecting_break_to_end');
       if (!fetchedData.types || fetchedData.types.filter(t => t.type === 'break_end').length === 0) {
           throw new Error("Failed to load break types or no 'Break End' types found.");
       }
      setShiftStatus('selecting_break_to_end');
    } catch (error) {
       toast.error(`Error preparing break end: ${error instanceof Error ? error.message : String(error)}`);
      setShiftStatus(statusBeforeSelection);
    } finally {
       setActiveLoadingAction(null);
    }
  };

  const handleConfirmBreakEnd = async () => {
    if (!tempSelectedBreakTypeId) { toast.warn("Please select a Break Type to end the break."); return; }
     const currentShiftInstanceId = activeShiftInstanceId ?? getLocalStorageItem('activeShiftInstanceId');
     const currentShiftId = selectedShiftId ?? getLocalStorageItem('selectedShiftId');
     if (!currentShiftInstanceId) { toast.error("Error: Shift context lost (Instance ID). Please refresh."); return; }
     if (!currentShiftId) { toast.error("Error: Shift context lost (Shift ID). Please refresh."); return; }

    setActiveLoadingAction('confirmEndBreak');
    const breakEndTimeStr = formatDateWithTime24h(new Date());
    try {
        const breakEndPayload = {
            parent_attendance_id: currentShiftInstanceId,
            attendance_type_id: tempSelectedBreakTypeId,
            attendance_datetime: breakEndTimeStr,
            employee_attendance_notes: "Break Ended",
            shift_id: currentShiftId,
        };
        await add_attendance_shifts(breakEndPayload);
        toast.success("Break ended! Syncing...");
        attendanceOptionsMutation.mutate();
        setTempSelectedBreakTypeId(null);
    } catch (error:any) {
        const errorMsg = error?.response?.data?.message || (error instanceof Error ? error.message : String(error));
        toast.error(`Error ending break: ${errorMsg}`);
    } finally {
        setActiveLoadingAction(null);
    }
  };

const handleInitiateEndShift = async () => {
  if (shiftStatus === 'ended' || shiftStatus === 'selecting_end_shift') return;
  const currentShiftIdFromState = selectedShiftId ?? getLocalStorageItem('selectedShiftId');
   if (!currentShiftIdFromState) { toast.error("Error: Cannot end shift without an active shift."); return; }
  setStatusBeforeSelection(shiftStatus);
  setActiveLoadingAction('initiateEndShift');
  try {
    const fetchedData = await fetchRequiredData('selecting_end_shift');
    const currentTypes = fetchedData.types;
    if (!currentTypes) throw new Error("Could not load attendance types required for ending shift.");
    const shiftEndType = currentTypes.find(type => type.type === "shift_end");
    if (!shiftEndType) throw new Error("'Shift End' attendance type not found. Please contact support.");
    setTempSelectedShiftId(currentShiftIdFromState); // Keep current shift ID for display
    setShiftStatus('selecting_end_shift');
  } catch (error) {
    toast.error(`Error preparing to end shift: ${error instanceof Error ? error.message : String(error)}.`);
    setShiftStatus(statusBeforeSelection);
    setTempSelectedShiftId(null); setTempSelectedShiftTypeId(null);
  } finally {
    setActiveLoadingAction(null);
  }
};

const handleConfirmEndShift = async () => {
  if (shiftStatus !== 'selecting_end_shift') return;
  let shiftEndTypeIdToUse = tempSelectedShiftTypeId;
  if (!shiftEndTypeIdToUse) {
      const shiftEndTypeRecord = attendanceTypes.find(type => type.type === "shift_end");
      if (!shiftEndTypeRecord) {
          toast.error("Configuration Error: 'Shift End' type not found. Please contact support.");
          setShiftStatus(statusBeforeSelection); return;
      }
      shiftEndTypeIdToUse = shiftEndTypeRecord.id;
  }
  const currentShiftInstanceId = activeShiftInstanceId ?? getLocalStorageItem('activeShiftInstanceId');
  const currentShiftId = selectedShiftId ?? getLocalStorageItem('selectedShiftId');
  if (!currentShiftInstanceId) { toast.error("Error: Shift context lost (Instance ID). Please refresh."); return; }
  if (!currentShiftId) { toast.error("Error: Shift context lost (Shift ID). Please refresh."); return; }

  setActiveLoadingAction('confirmEndShift');
  const endTimeStr = formatDateWithTime24h(new Date());
  try {
    const shiftEndPayload = {
      parent_attendance_id: currentShiftInstanceId,
      shift_id: currentShiftId,
      attendance_type_id: shiftEndTypeIdToUse,
      attendance_datetime: endTimeStr,
      employee_attendance_notes: "Shift Ended",
    };
    await add_attendance_shifts(shiftEndPayload);
    toast.success("Shift ended successfully! Syncing...");
    attendanceOptionsMutation.mutate();
    setTempSelectedShiftId(null); setTempSelectedShiftTypeId(null); setTempSelectedBreakTypeId(null);
  } catch (error:any) {
    const errorMsg = error?.response?.data?.message || (error instanceof Error ? error.message : String(error));
    toast.error(`Error ending shift: ${errorMsg}`);
  } finally {
     setActiveLoadingAction(null);
  }
};

  useEffect(() => {
    // This useEffect is for restoring from localStorage *before* the first API call completes.
    // Once lastProcessedAttendanceRef.current has a value (even null), API is the source of truth.
    if (lastProcessedAttendanceRef.current === undefined && userId) {
        const storedStartTime = getLocalStorageItem('shiftStartTime');
        const storedShiftId = getLocalStorageItem('selectedShiftId');
        const storedInstanceId = getLocalStorageItem('activeShiftInstanceId');

        if (storedStartTime && storedShiftId && storedInstanceId) {
            // console.log("Restoring initial state from localStorage as no API data processed yet.");
            setShiftStartTime(storedStartTime);
            setSelectedShiftId(storedShiftId);
            if (getLocalStorageItem('selectedShiftTypeId')) setSelectedShiftTypeId(getLocalStorageItem('selectedShiftTypeId'));
            setActiveShiftInstanceId(storedInstanceId);

            if (getLocalStorageItem('breakStartTime') && getLocalStorageItem('selectedBreakTypeId')) {
                setSelectedBreakTypeId(getLocalStorageItem('selectedBreakTypeId'));
                setShiftStatus('on_break');
            } else {
                setShiftStatus('in_progress');
            }
            // Pre-fetch dropdown data if not already loaded by another mechanism
            if (!shiftsList || shiftsList.length === 0) fetchShiftData();
            if (!attendanceTypes || attendanceTypes.length === 0) fetchAttendanceTypes();
        } else {
            setShiftStatus('not_started');
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, getLocalStorageItem]); // Dependencies for localStorage restoration

 // --- Render Function ---
 const renderControls = () => {
    const isMutationLoading = attendanceOptionsMutation.isLoading;
    // isMajorOperationInProgress: True if a user action, mutation, or INITIAL query load is happening.
    // Excludes background refetchInterval polls unless it's the very first load.
    const isMajorOperationInProgress =
        activeLoadingAction !== null ||
        isMutationLoading ||
        (isAttendanceQueryInitiallyLoading && lastProcessedAttendanceRef.current === undefined);

    const getShiftTitle = (id: string | number | null): string => {
        if (!id) return "";
        // Loading text for dropdowns if data is being actively fetched for them or during initial load
        const isLoadingDropdownData = activeLoadingAction === 'fetchData' ||
                                     (isMajorOperationInProgress && (!shiftsList || shiftsList.length === 0));
        if (isLoadingDropdownData && (!shiftsList || shiftsList.length === 0)) return 'Loading Shifts...';
        if (!Array.isArray(shiftsList) || shiftsList.length === 0) return 'No Shifts';

        let shift = shiftsList.find(s => String(s.id) === String(id));
        return shift?.title ?? shift?.shift_title ?? `Shift ID: ${id}`;
    }
    const getAttendanceTypeTitle = (id: string | number | null): string => {
        if (!id) return "";
        const isLoadingDropdownData = activeLoadingAction === 'fetchData' ||
                                     (isMajorOperationInProgress && (!attendanceTypes || attendanceTypes.length === 0));
        if (isLoadingDropdownData && (!attendanceTypes || attendanceTypes.length === 0)) return 'Loading Types...';
        if (!Array.isArray(attendanceTypes) || attendanceTypes.length === 0) return 'No Types';

        let type = attendanceTypes.find(t => String(t.id) === String(id));
        return type?.title ?? `Type ID: ${id}`;
    }

    const renderSelectionUI = (
        actionType: 'start_shift' | 'start_break' | 'end_break' | 'end_shift'
    ) => {
        let confirmHandler: () => void = () => {};
        let confirmActionType: LoadingAction = null;
        let confirmButtonText = "Confirm";
        let isConfirmDisabledSpecific = false; // For specific validation

        switch (actionType) {
            case 'start_shift':
                confirmHandler = handleConfirmShiftStart; confirmButtonText = "Start Shift";
                confirmActionType = 'confirmStartShift';
                isConfirmDisabledSpecific = !tempSelectedShiftId;
                break;
            case 'start_break':
                confirmHandler = handleConfirmBreakStart; confirmButtonText = "Start Break";
                confirmActionType = 'confirmStartBreak';
                isConfirmDisabledSpecific = !tempSelectedBreakTypeId;
                break;
            case 'end_break':
                confirmHandler = handleConfirmBreakEnd; confirmButtonText = "End Break";
                confirmActionType = 'confirmEndBreak';
                 isConfirmDisabledSpecific = !tempSelectedBreakTypeId;
                break;
            case 'end_shift':
                confirmHandler = handleConfirmEndShift; confirmButtonText = "Confirm End Shift";
                confirmActionType = 'confirmEndShift';
                // If multiple end types exist, one must be selected
                if (attendanceTypes.filter(t => t.type === 'shift_end').length > 1 && !tempSelectedShiftTypeId) {
                    isConfirmDisabledSpecific = true;
                }
                break;
        }

        const confirmButtonDisabled = isMajorOperationInProgress || isConfirmDisabledSpecific;
        const cancelButtonDisabled = isMajorOperationInProgress && activeLoadingAction !== null && activeLoadingAction !== 'fetchData';


        const displayShiftId = (actionType === 'start_shift' || tempSelectedShiftId)
            ? tempSelectedShiftId
             : (selectedShiftId ?? null);
        const displayShiftTitle = getShiftTitle(displayShiftId);
        const needsShiftDisplay = ['selecting_break_to_start', 'selecting_break_to_end', 'selecting_end_shift'].includes(shiftStatus);

        const showLoadingPlaceholderForDropdowns = activeLoadingAction === 'fetchData' &&
                                                 ( (actionType === 'start_shift' && shiftsList.length === 0) ||
                                                   ( (actionType === 'start_break' || actionType === 'end_break') && attendanceTypes.filter(t => t.type === (actionType === 'start_break' ? 'break_start' : 'break_end')).length === 0 )
                                                 );

        if (showLoadingPlaceholderForDropdowns) {
             return (
                 <div className='d-flex justify-content-center align-items-center p-3 w-100'>
                    <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></span>
                    <span>Loading options...</span>
                 </div>
             );
        }

        return (
            <>
              <div className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center p-2 bg-light border rounded shadow-sm ms-2 flex-wrap' style={{zIndex: 10}}>
                  {actionType === 'start_shift' && (
                      <div className="mb-2 mb-sm-0 me-sm-2 flex-grow-1">
                          <label htmlFor="selectShiftTitle" className="form-label visually-hidden">Select Shift</label>
                          <select id="selectShiftTitle"
                                  className={`form-select form-select-sm ${!tempSelectedShiftId && shiftStatus === 'selecting_shift' ? 'is-invalid' : ''}`}
                                  onChange={handleSelectShiftTitleDropdown}
                                  value={tempSelectedShiftId ?? ""}
                                  disabled={isMajorOperationInProgress}
                                  aria-label="Select Shift Title" required >
                              <option value="" disabled>Select Shift...</option>
                              {(Array.isArray(shiftsList) && shiftsList.length > 0)
                                ? shiftsList.map((shift) => (<option key={shift.id} value={shift.id}>{shift.title ?? shift.shift_title ?? `ID: ${shift.id}`}</option>))
                                : <option disabled>{(isMajorOperationInProgress && shiftsList.length === 0) ? "Loading shifts..." : "No shifts available"}</option>}
                          </select>
                      </div>
                  )}
                   {needsShiftDisplay && (
                       <div className="mb-2 mb-sm-0 me-sm-2 flex-grow-1">
                           <label htmlFor="displayShiftTitle" className="form-label visually-hidden">Current Shift</label>
                          <input type="text" id="displayShiftTitle" className="form-control form-control-sm" value={displayShiftTitle || 'N/A'} disabled readOnly aria-label="Current Shift Title"/>
                      </div>
                  )}
                  {(actionType === 'start_break' || actionType === 'end_break') && (
                    <div className="mb-2 mb-sm-0 me-sm-2 flex-grow-1">
                        <label htmlFor="selectBreakType" className="form-label visually-hidden">Select Break Type</label>
                      <select id="selectBreakType"
                              className={`form-select form-select-sm ${!tempSelectedBreakTypeId && (shiftStatus === 'selecting_break_to_start' || shiftStatus === 'selecting_break_to_end') ? 'is-invalid' : ''}`}
                              onChange={handleSelectAttendanceTypeDropdown}
                              value={tempSelectedBreakTypeId ?? ""}
                              disabled={isMajorOperationInProgress}
                              aria-label="Select Break Type" required >
                        <option value="" disabled>Select Break Type...</option>
                        {(Array.isArray(attendanceTypes) && attendanceTypes.length > 0) ?
                          attendanceTypes.filter((type) => actionType === 'start_break' ? type.type === 'break_start' : actionType === 'end_break' ? type.type === 'break_end' : false)
                                       .map((type) => (<option key={type.id} value={type.id}> {type.title ?? `ID: ${type.id}`} </option>))
                          : <option disabled>{ (isMajorOperationInProgress && attendanceTypes.length === 0) ? "Loading types..." : "No types available"}</option>
                        }
                         {Array.isArray(attendanceTypes) && attendanceTypes.filter(t => actionType === 'start_break' ? t.type === 'break_start' : t.type === 'break_end').length === 0 && !isMajorOperationInProgress && <option disabled>No relevant types found</option>}
                      </select>
                    </div>
                  )}
                   {actionType === 'end_shift' && attendanceTypes.filter(t => t.type === 'shift_end').length > 1 && (
                      <div className="mb-2 mb-sm-0 me-sm-2 flex-grow-1">
                          <label htmlFor="selectEndShiftType" className="form-label visually-hidden">Select End Type</label>
                          <select id="selectEndShiftType"
                                  className="form-select form-select-sm"
                                  onChange={handleSelectAttendanceTypeDropdown}
                                  value={tempSelectedShiftTypeId ?? ""}
                                  disabled={isMajorOperationInProgress}
                                  aria-label="Select End Shift Type" required>
                              <option value="" disabled>Select End Type...</option>
                              {attendanceTypes.filter(t => t.type === 'shift_end').map(type => (
                                  <option key={type.id} value={type.id}>{type.title}</option>
                              ))}
                          </select>
                      </div>
                   )}
                   {actionType === 'end_shift' && attendanceTypes.filter(t => t.type === 'shift_end').length <= 1 && (
                       <div className="mb-2 mb-sm-0 me-sm-2 flex-grow-1 text-muted align-self-center">
                          Ending: {getShiftTitle(selectedShiftId)}
                      </div>
                   )}
                   <div className={`d-grid d-sm-block mt-2 mt-sm-0 ms-sm-auto align-self-center`}>
                      <button type="button" className={`btn btn-sm ${actionType === 'end_shift' ? 'btn-danger' : 'btn-success'}`} onClick={confirmHandler} disabled={confirmButtonDisabled} title={confirmButtonText} >
                          {activeLoadingAction === confirmActionType && (<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>)}
                          {confirmButtonText}
                      </button>
                   </div>
              </div>
              <button type="button" className='btn btn-sm btn-outline-secondary ms-2 align-self-center' onClick={handleCancelSelection} disabled={cancelButtonDisabled} title="Cancel Action" >
                  Cancel
              </button>
            </>
        );
    };

    switch (shiftStatus) {
      case 'selecting_shift':       return renderSelectionUI('start_shift');
      case 'selecting_break_to_start': return renderSelectionUI('start_break');
      case 'selecting_break_to_end':  return renderSelectionUI('end_break');
      case 'selecting_end_shift':   return renderSelectionUI('end_shift');

      case 'in_progress':
        return (
             <div className='d-flex align-items-center justify-content-start w-100 px-3'>
                <div className='d-flex align-items-center me-4'>
                    <button type="button" className='btn btn-icon btn-warning w-40px h-40px me-1' onClick={handleInitiateBreakStart} disabled={isMajorOperationInProgress} title="Start Break">
                        {activeLoadingAction === 'initiateStartBreak' ? <span className="spinner-border spinner-border-sm"></span> : <i className="fas fa-pause fs-4"></i>}
                    </button>
                    <span className=''>Start Break</span>
                </div>
                <div className='d-flex align-items-center'>
                    <button type="button" className='btn btn-icon btn-danger w-40px h-40px me-1' onClick={handleInitiateEndShift} disabled={isMajorOperationInProgress} title="End Shift">
                        {activeLoadingAction === 'initiateEndShift' ? <span className="spinner-border spinner-border-sm"></span> : <i className="fa-solid fa-stop fs-4"></i>}
                    </button>
                    <span className=''>End Shift</span>
                </div>
            </div>
        );

      case 'on_break':
        const breakTypeTitle = getAttendanceTypeTitle(selectedBreakTypeId);
        return (
          <div className='d-flex align-items-center justify-content-between w-100 px-3'>
            <div className='d-flex align-items-center'>
               <button type="button" className='btn btn-icon btn-success w-40px h-40px' onClick={handleInitiateBreakEnd} disabled={isMajorOperationInProgress} title="End Break">
                 {activeLoadingAction === 'initiateEndBreak' ? <span className="spinner-border spinner-border-sm"></span> : <i className="fa-solid fa-play fs-4"></i>}
               </button>
            </div>
            <span className='mx-3 text-muted'>
                 On Break {breakTypeTitle ? `(${breakTypeTitle})` : (isMajorOperationInProgress && !selectedBreakTypeId ? '(Loading...)' : '')}
             </span>
            <div className='d-flex align-items-center'>
              <button type="button" className='btn btn-icon btn-danger w-40px h-40px me-1' onClick={handleInitiateEndShift} disabled={isMajorOperationInProgress} title="End Shift">
                {activeLoadingAction === 'initiateEndShift' ? <span className="spinner-border spinner-border-sm"></span> : <i className="fa-solid fa-stop fs-4"></i>}
              </button>
              <span className='d-none d-md-inline'>End Shift</span>
            </div>
          </div>
        );

      case 'ended':
      case 'not_started':
      default:
        // Show spinner if initial data is loading OR if initiating start shift
        const showInitialStartSpinner = isMajorOperationInProgress || activeLoadingAction === 'initiateStartShift';
        return (
            <div className='d-flex align-items-center ms-3'>
              <button type="button" className={`btn btn-icon btn-success w-40px h-40px me-2`} onClick={handleInitiateShiftStart} disabled={isMajorOperationInProgress} title={"Start Shift"} >
                {showInitialStartSpinner ? <span className="spinner-border spinner-border-sm"></span> : <i className={`fa-solid fa-play fs-4`}></i>}
              </button>
               <span className=''>Start Shift</span>
            </div>
        );
    }
  };

  if (!userId) {
    return (
        <div className='d-flex align-items-center my-2 my-lg-0 w-100 justify-content-center p-3 text-muted'>
            User not identified. Shift controls unavailable.
        </div>
    );
  }

  return (
    <div className='d-flex align-items-center my-2 my-lg-0 w-100'>
        {renderControls()}
    </div>
    );
};